import { useRef, useCallback } from 'react';
import { useSettings } from '../components/layout/Settingscontext';

export function useSound() {
  const settings = useSettings();
  const sfxEnabled = settings ? settings.sfxEnabled : true; // safe fallback if Provider is missing
  const ctxRef = useRef(null);

  const getCtx = () => {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return ctxRef.current;
  };

  const playTone = useCallback((freq, type = 'sine', duration = 0.12, volume = 0.18) => {
    if (!sfxEnabled) return;
    try {
      const ctx = getCtx();

      // Browsers start AudioContext "suspended" until a user gesture resumes it.
      // Without this, tones silently do nothing on first interaction.
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch (err) {
      console.warn('useSound: tone failed to play', err);
    }
  }, [sfxEnabled]);

  const playHover   = useCallback(() => playTone(880, 'sine', 0.07, 0.08), [playTone]);
  const playClick   = useCallback(() => playTone(440, 'triangle', 0.14, 0.22), [playTone]);
  const playToggle  = useCallback(() => playTone(660, 'square', 0.08, 0.15), [playTone]);
  const playDrop    = useCallback(() => { playTone(520, 'sine', 0.1, 0.2); setTimeout(() => playTone(660, 'sine', 0.1, 0.15), 80); }, [playTone]);
  const playError   = useCallback(() => { [400, 340, 280].forEach((f, i) => setTimeout(() => playTone(f, 'sawtooth', 0.2, 0.2), i * 140)); }, [playTone]);
  const playVictory = useCallback(() => { [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => playTone(f, 'sine', 0.28, 0.25), i * 130)); }, [playTone]);
  const playDefeat  = useCallback(() => { [400, 340, 280].forEach((f, i) => setTimeout(() => playTone(f, 'sawtooth', 0.2, 0.2), i * 140)); }, [playTone]);
  const playFanfare = useCallback(() => { [523, 659, 784, 1047, 1318].forEach((f, i) => setTimeout(() => playTone(f, 'sine', 0.3, 0.22), i * 110)); }, [playTone]);
  const playCoin    = useCallback(() => playTone(1200, 'square', 0.06, 0.1), [playTone]);
  const playLock    = useCallback(() => playTone(220, 'sawtooth', 0.18, 0.18), [playTone]);

  return { playHover, playClick, playToggle, playDrop, playError, playVictory, playDefeat, playFanfare, playCoin, playLock };
}